import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Col, Form, Row } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { NumericFormat as NumberFormat } from "react-number-format";

import ConditionalButton from "../../../layout/conditional-button";
import { canManageFeradelAdministration, canViewFeradelAdministration } from "../../../constants/permissions";
import { sourceDestroy, sourceGet, sourceSave } from "../../../actions/areas";
import { isGuid } from "../../../utils/string";
import CsLineIcons from "../../../cs-line-icons";

const toInputDate = (value) => {
    if (!value) return "";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
};

const emptyArea = () => ({
    uuid: uuidv4(),
    licenseeUuid: "",
    name: "",
    abbreviation: "",
    activeDate: toInputDate(new Date()),
    inactiveDate: "",
    population: 0,
    rate: 1,
    licenseFee: 0,
    platformFee: 1
});

class AreaSourceEditor extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            data: emptyArea(),
            error: null,
            loading: true
        };
    }

    componentDidMount = () => {
        this.loadData();
    }

    componentDidUpdate = (oldProps) => {
        if (oldProps.match?.params?.uuid !== this.props.match?.params?.uuid) {
            this.loadData();
            return;
        }

        const oldPermissions = (oldProps.auth?.permissions || []).join("|");
        const newPermissions = (this.props.auth?.permissions || []).join("|");
        if (oldPermissions !== newPermissions && this.canViewArea && this.state.loading) {
            this.loadData();
        }
    }

    get uuid() {
        return this.props.match?.params?.uuid;
    }

    get isNew() {
        return !isGuid(this.uuid);
    }

    get canViewArea() {
        return canViewFeradelAdministration(this.props.auth?.permissions);
    }

    get canManageArea() {
        return canManageFeradelAdministration(this.props.auth?.permissions);
    }

    loadData = async () => {
        if (!this.canViewArea) return;

        this.setState({ loading: true });
        let data = emptyArea();

        if (!this.isNew) {
            const loaded = await this.props.sourceGet({ uuid: this.uuid });
            if (loaded?.uuid) {
                data = {
                    ...data,
                    ...loaded,
                    licenseeUuid: loaded.licenseeUuid || "",
                    activeDate: toInputDate(loaded.activeDate),
                    inactiveDate: toInputDate(loaded.inactiveDate)
                };
            }
        }

        this.setState({ data, loading: false });
    }

    onChange = (e) => {
        if (!this.canManageArea) return;

        const { name, value } = e.target;
        this.setState(prev => ({
            data: {
                ...prev.data,
                [name]: value
            },
            error: null
        }));
    }

    onNumberChange = (name, value) => {
        this.onChange({ target: { name, value: value ?? 0 } });
    }

    validate = () => {
        if (!String(this.state.data.name || "").trim()) return "Name is required.";
        if (!String(this.state.data.abbreviation || "").trim()) return "Abbreviation is required.";
        return null;
    }

    onSaveClick = async () => {
        if (!this.canManageArea) return;

        const error = this.validate();
        if (error) {
            this.setState({ error });
            return;
        }

        const saved = await this.props.sourceSave({
            ...this.state.data,
            licenseeUuid: isGuid(this.state.data.licenseeUuid) ? this.state.data.licenseeUuid : null,
            inactiveDate: this.state.data.inactiveDate || null
        });

        if (saved?.success === false || !saved?.uuid) {
            this.setState({ error: saved?.error || "The area could not be saved." });
            return;
        }

        window.location.href = `/dashboard/settings/areas/${saved.uuid}`;
    }

    onDeleteClick = async () => {
        if (!this.canManageArea || this.isNew) return;

        await this.props.sourceDestroy({ uuid: this.state.data.uuid });
        window.location.href = "/dashboard/settings/areas";
    }

    render() {
        if (!this.canViewArea) {
            return <Col xs="12">Area settings not available.</Col>;
        }

        if (this.state.loading) {
            return <Col xs="12">Loading...</Col>;
        }

        return (
            <>
                <Col xs="12" className="licensee-editor-toolbar">
                    <ConditionalButton className="licensee-save-button" enabledVariant="primary" tooltip="Save area" display={this.canManageArea} enabled={this.canManageArea} onEnabledClick={this.onSaveClick} icon="check-square">Save Changes</ConditionalButton>
                    <ConditionalButton className="licensee-delete-button" enabledVariant="outline-danger" tooltip="Delete area" display={this.canManageArea && !this.isNew} enabled={this.canManageArea && !this.isNew} onEnabledClick={this.onDeleteClick} icon="close-circle">Delete</ConditionalButton>
                </Col>
                {this.state.error ? (
                    <Col xs="12">
                        <div className="alert alert-danger">{this.state.error}</div>
                    </Col>
                ) : null}
                <Col xs="12" className="licensee-editor">
                    <section className="licensee-editor-card">
                        <header className="licensee-editor-card-header">
                            <span><CsLineIcons icon="usa-map" size="21" /></span>
                            <h2>Area Information</h2>
                        </header>
                    <Row className="g-3">
                        <Col xs="12" md="8">
                            <div className="mb-3 licensee-field">
                                <Form.Label>NAME</Form.Label>
                                <Form.Control type="text" name="name" disabled={!this.canManageArea} value={this.state.data.name || ""} onChange={this.onChange} />
                            </div>
                        </Col>
                        <Col xs="12" md="4">
                            <div className="mb-3 licensee-field">
                                <Form.Label>ABBREVIATION</Form.Label>
                                <Form.Control type="text" name="abbreviation" disabled={!this.canManageArea} value={this.state.data.abbreviation || ""} onChange={this.onChange} />
                            </div>
                        </Col>
                        <Col xs="12" md="6">
                            <div className="mb-3 licensee-field">
                                <Form.Label>LICENSEE UUID</Form.Label>
                                <Form.Control type="text" name="licenseeUuid" disabled={!this.canManageArea} value={this.state.data.licenseeUuid || ""} onChange={this.onChange} />
                            </div>
                        </Col>
                        <Col xs="12" md="3">
                            <div className="mb-3 licensee-field">
                                <Form.Label>ACTIVE DATE</Form.Label>
                                <Form.Control type="date" name="activeDate" disabled={!this.canManageArea} value={this.state.data.activeDate || ""} onChange={this.onChange} />
                            </div>
                        </Col>
                        <Col xs="12" md="3">
                            <div className="mb-3 licensee-field">
                                <Form.Label>INACTIVE DATE</Form.Label>
                                <Form.Control type="date" name="inactiveDate" disabled={!this.canManageArea} value={this.state.data.inactiveDate || ""} onChange={this.onChange} />
                            </div>
                        </Col>
                        <Col xs="12" md="3">
                            <div className="mb-3 licensee-field">
                                <Form.Label>POPULATION</Form.Label>
                                <NumberFormat displayType="input" type="text" disabled={!this.canManageArea} className="form-control text-end" thousandSeparator={false} decimalScale={0} value={this.state.data.population ?? 0} onValueChange={(e) => this.onNumberChange("population", e.floatValue)} />
                            </div>
                        </Col>
                        <Col xs="12" md="3">
                            <div className="mb-3 licensee-field">
                                <Form.Label>RATE</Form.Label>
                                <NumberFormat displayType="input" type="text" disabled={!this.canManageArea} className="form-control text-end" thousandSeparator={false} decimalScale={2} value={this.state.data.rate ?? 0} onValueChange={(e) => this.onNumberChange("rate", e.floatValue)} />
                            </div>
                        </Col>
                        <Col xs="12" md="3">
                            <div className="mb-3 licensee-field">
                                <Form.Label>LICENSE FEE</Form.Label>
                                <NumberFormat displayType="input" type="text" disabled={!this.canManageArea} className="form-control text-end" prefix="$" thousandSeparator={false} decimalScale={2} value={this.state.data.licenseFee ?? 0} onValueChange={(e) => this.onNumberChange("licenseFee", e.floatValue)} />
                            </div>
                        </Col>
                        <Col xs="12" md="3">
                            <div className="mb-3 licensee-field">
                                <Form.Label>PLATFORM FEE</Form.Label>
                                <NumberFormat displayType="input" type="text" disabled={!this.canManageArea} className="form-control text-end" thousandSeparator={false} decimalScale={3} value={this.state.data.platformFee ?? 0} onValueChange={(e) => this.onNumberChange("platformFee", e.floatValue)} />
                            </div>
                        </Col>
                    </Row>
                    </section>
                </Col>
            </>
        );
    }
}

AreaSourceEditor.propTypes = {
    auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default connect(mapStateToProps, { sourceDestroy, sourceGet, sourceSave })(AreaSourceEditor);
